import { useEffect, useId, useState } from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { NumberInput } from '../ui/NumberInput';
import { btnGhost, btnIcon, inputCls } from '../ui/styles';
import { SettingNumber } from './SettingNumber';
import { SubscriptionMarginHint } from './SubscriptionMarginHint';
import { useSettings } from './useSettings';
import type { PauseOption } from '../../core/types/site';

/** "4, 6, 8" → [4, 6, 8]; null bij iets ongeldigs (leeg, geen getal, buiten 1–52). */
const parseWeeks = (raw: string): number[] | null => {
  const parts = raw.split(/[,\s]+/).filter(Boolean);
  if (parts.length === 0) return null;
  const weeks = parts.map(Number);
  if (weeks.some(w => !Number.isInteger(w) || w < 1 || w > 52)) return null;
  return Array.from(new Set(weeks)).sort((a, b) => a - b);
};

/** Instellingen › Webshop & verzending › Bewust Abonnement. */
export function SubscriptionSettings() {
  const {
    policy,
    patchPolicy
  } = useSettings();
  const sub = policy.subscription;
  const set = (patch: Partial<typeof sub>) => patchPolicy('subscription', patch);

  // Ritmes als gewone tekst; pas bij verlaten van het veld bewaard, zodat typen niet hapert.
  const intervalsId = useId();
  const [intervalsText, setIntervalsText] = useState(sub.intervals.join(', '));
  const [intervalsError, setIntervalsError] = useState<string | null>(null);
  useEffect(() => setIntervalsText(sub.intervals.join(', ')), [sub.intervals]);
  const commitIntervals = () => {
    const weeks = parseWeeks(intervalsText);
    if (!weeks) {
      setIntervalsError('Vul hele weken in, gescheiden door een komma, bijv. 4, 6, 8.');
      return;
    }
    setIntervalsError(null);
    // Het standaardritme moet een van de keuzes blijven.
    const fallback = weeks.includes(sub.defaultIntervalWeeks) ? sub.defaultIntervalWeeks : weeks[0];
    set({
      intervals: weeks,
      defaultIntervalWeeks: fallback
    });
  };
  const setPause = (i: number, patch: Partial<PauseOption>) => set({
    pauseOptions: sub.pauseOptions.map((p, j) => j === i ? {
      ...p,
      ...patch
    } : p)
  });
  return <Panel title="Bewust Abonnement" description="De regels van het productabonnement. Klanten zien ze in de winkel en in Mijn omgeving." data-id="element-4411" data-mp="336zjfvnkmth1">
      <div className="grid gap-5 sm:grid-cols-2" data-id="element-4412" data-mp="3cw9b5aei6a4m">
        <SettingNumber label="Minimumbedrag per doos (€)" hint="Slim bijvullen stuurt nooit een kleinere doos. Volgt uit je marge en wat een pakket kost." value={sub.minBoxValue} onChange={v => set({
        minBoxValue: v
      })} min={0} max={500} data-id="element-4413" data-mp="3cx19gszhilyl" />
        <SettingNumber label="Hoogstens per product per doos" hint="Meer tegelijk blijft niet vers." value={sub.maxUnitsPerProduct} onChange={v => set({
        maxUnitsPerProduct: v
      })} min={1} max={4} data-id="element-4414" data-mp="3qwk4ql7762th" />
        <SettingNumber label="Marge vóór het op is (dagen)" hint="Zo lang voordat iets op raakt, ligt het nieuwe er al." value={sub.bufferDays} onChange={v => set({
        bufferDays: v
      })} max={21} data-id="element-4415" data-mp="21gax0s4gwxwj" />
        <SettingNumber label="Cadeau bij elke … levering" hint="Bijvoorbeeld 3: de derde, zesde, negende levering." value={sub.giftEvery} onChange={v => set({
        giftEvery: v
      })} min={1} max={24} data-id="element-4416" data-mp="1p4s1zv1vqlb0" />
        <Field label="Het cadeau in die doos" hint="Midden in een zin: “er zit … bij”. Bijvuldozen tellen niet mee voor de cadeaustaffel, dus dit mag iets royaler zijn." data-id="element-4417" data-mp="2cv4ivvf3rsdq">
          <input value={sub.giftDescription} onChange={e => set({
          giftDescription: e.target.value
        })} onBlur={e => !e.target.value.trim() && set({
          giftDescription: 'een cadeau van Sarah'
        })} placeholder="Bijv. een reisformaat serum" className={inputCls} data-id="element-4418" data-mp="22xqc6nizehym" />
        </Field>
        <SettingNumber label="Huidchecks per jaar (video)" value={sub.videoChecksPerYear} onChange={v => set({
        videoChecksPerYear: v
      })} max={12} data-id="element-4419" data-mp="mios8il56vhi" />
        <SettingNumber label="Eerste levering na (dagen)" hint="Na starten of hervatten." value={sub.leadDays} onChange={v => set({
        leadDays: v
      })} max={60} data-id="element-4420" data-mp="2g68glmuqnj92" />
        <SettingNumber label="Aanpassen tot (dagen voor levering)" hint="Daarna ligt de levering vast." value={sub.changeUntilDays} onChange={v => set({
        changeUntilDays: v
      })} max={30} data-id="element-4421" data-mp="26crmsiulq45e" />
        <div data-id="element-4422" data-mp="2k7alaj7fcnif">
          <label htmlFor={intervalsId} className="text-[13px] font-medium text-ink" data-id="element-4423" data-mp="2k5poso1juuhx">
            Ritmes om uit te kiezen (weken)
          </label>
          <span className="mt-0.5 block text-[12px] leading-snug text-muted" data-id="element-4424" data-mp="18rc2hnmg2n40">
            Gescheiden door een komma. Het systeem stelt het kortste ritme voor waarbij elke doos het minimum haalt.
          </span>
          <input id={intervalsId} value={intervalsText} onChange={e => setIntervalsText(e.target.value)} onBlur={commitIntervals} inputMode="numeric" aria-invalid={intervalsError ? true : undefined} aria-describedby={intervalsError ? `${intervalsId}-error` : undefined} className={`${inputCls} mt-1.5 sm:max-w-[16rem]`} data-id="element-4425" data-mp="3rxy2mdlwvgud" />
          {intervalsError && <span id={`${intervalsId}-error`} role="alert" className="mt-1 block text-[12px] text-clay" data-id="element-4426" data-mp="397yje7cr4vyv">
              {intervalsError}
            </span>}
        </div>
        <Field label="Standaardritme" hint="Alleen als slim bijvullen niets kan uitrekenen (product zonder “gaat ± … mee”)." data-id="element-4427" data-mp="3i0tlis6xabkf">
          <select value={sub.defaultIntervalWeeks} onChange={e => set({
          defaultIntervalWeeks: Number(e.target.value)
        })} className={`${inputCls} sm:max-w-[11rem]`} data-id="element-4428" data-mp="3d77a13chwdq7">
            {sub.intervals.map(w => <option key={w} value={w} data-id="element-4429" data-mp="oggi4obgf8xj">
                Elke {w} weken
              </option>)}
          </select>
        </Field>
      </div>

      <SubscriptionMarginHint minBoxValue={sub.minBoxValue} giftEvery={sub.giftEvery} data-id="element-4430" data-mp="y7b37pcys2he" />

      <fieldset className="mt-6 border-t border-line pt-5" data-id="element-4431" data-mp="y9nifurc7yqr">
        <legend className="text-[13px] font-medium text-ink" data-id="element-4432" data-mp="1352uqi9p5ml2">Pauzekeuzes</legend>
        <p className="mt-0.5 text-[12px] text-muted" data-id="element-4433" data-mp="5jfaswufgwqf">In Mijn omgeving. De middelste wordt voorgesteld bij “ik heb nog genoeg”.</p>
        <ul className="mt-3 space-y-2" data-id="element-4434" data-mp="ai8mcjq5mdny">
          {sub.pauseOptions.map((p, i) => <li key={i} className="flex items-start gap-2" data-id="element-4435" data-mp="fg0ffh2jc5tq" data-mpi={i}>
              <label className="w-24 shrink-0" data-id="element-4436" data-mp="kfmu7uy0hu4n">
                <span className="sr-only" data-id="element-4437" data-mp="khhct2lt9a5k">Pauzekeuze {i + 1}: aantal weken</span>
                <NumberInput value={p.weeks} onChange={w => w !== undefined && setPause(i, {
              weeks: w
            })} min={1} max={52} data-id="element-4438" data-mp="1qux8d21ubf6d" />
              </label>
              <label className="min-w-0 flex-1" data-id="element-4439" data-mp="1vwwbcato4a2s">
                <span className="sr-only" data-id="element-4440" data-mp="3gc2hcgjpzu1o">Pauzekeuze {i + 1}: tekst voor de klant</span>
                <input value={p.label} onChange={e => setPause(i, {
              label: e.target.value
            })} className={inputCls} placeholder="Bijv. 2 weken" data-id="element-4441" data-mp="3bdr7w6xc7ljz" />
              </label>
              <button type="button" onClick={() => set({
            pauseOptions: sub.pauseOptions.filter((_, j) => j !== i)
          })} disabled={sub.pauseOptions.length <= 1} aria-label={`Pauzekeuze ${i + 1} weghalen`} title={sub.pauseOptions.length <= 1 ? 'Er moet minstens één pauzekeuze blijven' : undefined} className={`${btnIcon} mt-0.5`} data-id="element-4442" data-mp="31o7mati8pdlf">
                <Trash2Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4443" data-mp="2iqdhkro59o8u" />
              </button>
            </li>)}
        </ul>
        <button type="button" onClick={() => {
        const last = sub.pauseOptions[sub.pauseOptions.length - 1];
        const weeks = (last?.weeks ?? 2) + 2;
        set({
          pauseOptions: [...sub.pauseOptions, {
            weeks,
            label: `${weeks} weken`
          }]
        });
      }} className={`${btnGhost} mt-3`} data-id="element-4444" data-mp="lupholgytpgh">
          <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4445" data-mp="3c763v9eei9w1" />
          Pauzekeuze toevoegen
        </button>
      </fieldset>
    </Panel>;
}